import React, {useRef, memo, ElementType, Suspense} from "react"
import {ListItemIcon, SelectChangeEvent} from "@mui/material"
import {useTemplatedComponent} from "@next/templating"
import {MANIFEST} from "@manifest/header"
import {
    COUNTRY_SELECTOR_DRAWER_BREAKPOINT,
    CHEVRON_ICON_URL,
    COUNTRY_SELECT_DROPDOWN_DATA_GA,
} from "../../config/constants"
import {StyledIcon, SelectedMenuItem, StyledOutlinedInput, SelectedFormControl, styles} from "./component"
import MenuItemCountryFlagImg from "../MenuItemCountryFlagImg"
import connect from "./connect"
import {Country as CountryType} from "../../models/countryselector"
import env from "../../config/env"
import {useSearchCountrySelectorByKeyPress} from "../../utils/useSearchCountrySelectorByKeyPress"
import {useMediaQuery} from "../../hooks/useMediaQuery"

const CHEVRON_ICON = `${env.APP_BLOB_STORAGE_PATH}${CHEVRON_ICON_URL}`

type CountrySelectDropdownType = {
    territory: string
    textAlignment: string
    countriesList: CountryType[] | null
    selectedCountry: CountryType | null
    selectCountry: (countryCode: string) => void
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
}

export type SelectProps = {
    value?: string
    onChange: () => void
    onOpen: () => void
    onKeyPress: () => void
    MenuProps?: object
    input?: any
    IconComponent?: ElementType
    children?: any
}

let timerId: NodeJS.Timeout | number = 0

const CustomStyledIcon = props => (
    <StyledIcon {...props} className={`material-icons ${props.className}`} src={props.src} />
)

const MemoizedCustomStyledIcon = memo(CustomStyledIcon)

const passWithIconSrc = (chevronIcon: string) => {
    const WrappedCustomStyledIcon = props => <MemoizedCustomStyledIcon {...props} src={chevronIcon} />
    return WrappedCustomStyledIcon
}

export const CountrySelectDropdown = memo(
    ({
        territory,
        textAlignment,
        countriesList,
        selectedCountry,
        selectCountry,
        templating,
    }: CountrySelectDropdownType) => {
        const countryRef = useRef<HTMLInputElement>()
        const anchorDirectionBreakpoint = useMediaQuery(`(min-width:${COUNTRY_SELECTOR_DRAWER_BREAKPOINT})`)
        const itemAligment = textAlignment === "ltr" ? "left" : "right"
        const TemplatedSelect = useTemplatedComponent(templating, MANIFEST)

        const {onKeyPress} = useSearchCountrySelectorByKeyPress(countriesList, selectCountry)

        const handleChange = (event: SelectChangeEvent<unknown>): void => {
            selectCountry(event.target.value as string)
        }

        const onOpen = () => {
            timerId = setTimeout(() => {
                if (countryRef.current) {
                    countryRef.current.focus()
                }
                clearTimeout(timerId as NodeJS.Timeout)
            }, 500)
        }

        const setCountryRef = c => ref => {
            if (selectedCountry?.CountryCode === c.CountryCode) {
                countryRef.current = ref
            }
        }

        return (
            <SelectedFormControl variant="outlined">
                <Suspense>
                    <TemplatedSelect
                        value={selectedCountry ? selectedCountry.CountryCode : territory}
                        onChange={handleChange}
                        onOpen={onOpen}
                        onKeyPress={e => onKeyPress(e.key)}
                        MenuProps={{
                            keepMounted: false,
                            disablePortal: true,
                            disableScrollLock: true,
                            anchorOrigin: {
                                vertical: anchorDirectionBreakpoint ? "bottom" : "top",
                                horizontal: itemAligment,
                            },
                            transformOrigin: {
                                vertical: anchorDirectionBreakpoint ? "top" : "bottom",
                                horizontal: itemAligment,
                            },
                        }}
                        input={
                            <StyledOutlinedInput
                                data-testid="country-selector-select-input"
                                name="country-selector-select"
                                id="country-selector-select"
                            />
                        }
                        IconComponent={passWithIconSrc(CHEVRON_ICON)}
                        data-testid="country-selector-select-menu"
                    >
                        {/* eslint-disable-next-line react/display-name */}
                        {countriesList?.map((c: CountryType) => (
                            <SelectedMenuItem
                                data-testid={`country-selector-${c.Name}`}
                                data-ga-v1={COUNTRY_SELECT_DROPDOWN_DATA_GA}
                                data-ga-v2={c.DisplayText}
                                key={c.Country}
                                value={c.CountryCode}
                                sx={styles.root}
                                ref={setCountryRef(c)}
                            >
                                <ListItemIcon sx={styles.icon}>
                                    <MenuItemCountryFlagImg alt={c.CountryCode} src={c.iconUrl} />
                                </ListItemIcon>
                                {c.DisplayText}
                            </SelectedMenuItem>
                        ))}
                    </TemplatedSelect>
                </Suspense>
            </SelectedFormControl>
        )
    },
)
CountrySelectDropdown.displayName = "CountrySelectDropdown"

export default connect(CountrySelectDropdown)
